import {
  ApolloClient,
  type ApolloQueryResult,
  createHttpLink,
  gql,
  InMemoryCache,
} from '@apollo/client';
import { config } from 'config';
import jwtDecode from 'jwt-decode';
import * as lscache from 'lscache';
import {
  type ParsedSSOToken,
  SSOResponseCode,
  type SSOToken,
} from '../models/ssoTypes';
import type { Tenant } from '../models/tenant';
import { authStateStore } from '../state-store';

const httpLink = createHttpLink({
  uri: new URL(`/graphql`, config.appAdministrationBaseUrl).toString(),
  headers: {
    'X-Mosaic-Client-ID': 'MOSAIC_ADMIN_PORTAL', // TODO: To be removed after tripwire is no longer needed (search for 'ADMIN_API_TRIPWIRE')
  },
});

export const requestFailedMessage = `The request failed, please try again later. If the problem persists, please contact Axinom support.`;

export const setRequestFailedState = (
  signInStatus: boolean,
  message?: string,
): void => {
  authStateStore.setState({
    ssoContext: {
      signInStatus: signInStatus,
      ssoError: {
        code: SSOResponseCode.REQUEST_FAILED,
        message: message ?? requestFailedMessage,
      },
    },
  });
};

export const getSSOToken = async (): Promise<SSOToken> => {
  const ssoToken: SSOToken = {};

  try {
    if (config.axinomPortalBaseUrl === undefined) {
      throw new Error('SSO Endpoint could not be found.');
    }

    const ssoEndpoint = new URL(
      '/api/sso',
      config.axinomPortalBaseUrl,
    ).toString();

    // Disable cache when sending requests to fetch SSO Token.
    const headers = new Headers();
    headers.append('pragma', 'no-cache');
    headers.append('cache-control', 'no-cache');

    const ssoTokenResponse = await fetch(ssoEndpoint, {
      credentials: 'include',
      headers,
    });
    const ssoTokenResponsePayload = await ssoTokenResponse.json();

    if (ssoTokenResponse.status === 200) {
      // The SSO endpoint request succeeded

      if (ssoTokenResponsePayload.code === SSOResponseCode.SUCCESS) {
        // The SSO endpoint payload has success code
        authStateStore.setState({
          ssoContext: {
            signInStatus: true,
          },
        });
        ssoToken.token = ssoTokenResponsePayload.token;
        ssoToken.parsedToken = jwtDecode(ssoTokenResponsePayload.token);
      } else {
        // The SSO endpoint payload does not have a success code (i.e. not logged in, or not authorized)
        authStateStore.setState({
          ssoContext: {
            signInStatus: false,
            ssoError: {
              code: ssoTokenResponsePayload.code,
              message: ssoTokenResponsePayload.message,
            },
          },
        });
      }
    } else {
      // The request to the SSO endpoint did not result in a success (200). We categorize this as the same as REQUEST_FAILED, but use the error text
      // given by the fetch API.
      setRequestFailedState(false, ssoTokenResponsePayload.message);
    }
  } catch (error) {
    // Can be a CORS error.
    if (error instanceof Error) {
      setRequestFailedState(false, error.message);
    }
  }

  return ssoToken;
};

export const getTenantsBySSOToken = async (
  ssoToken: string,
): Promise<Tenant[] | undefined> => {
  const internalClient = new ApolloClient({
    cache: new InMemoryCache(),
    link: httpLink,
  });
  const parsedToken = jwtDecode(ssoToken) as ParsedSSOToken;
  let getTenantsBySSOTokenResponse: // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ApolloQueryResult<any> | undefined = undefined;
  try {
    getTenantsBySSOTokenResponse = await internalClient.query({
      query: gql`
        query GetTenantsBySSOToken($input: GetTenantsBySSOTokenInput!) {
          getTenantsBySSOToken(input: $input) {
            tenantAdminName
            tenants {
              name
              tenantId
            }
          }
        }
      `,
      variables: {
        input: {
          ssoToken: ssoToken,
        },
      },
    });
  } catch (error) {
    if (error instanceof Error) {
      setRequestFailedState(true, error.message);
    }
  }
  if (getTenantsBySSOTokenResponse !== undefined) {
    const tenants = getTenantsBySSOTokenResponse.data.getTenantsBySSOToken
      .tenants as Tenant[];
    if (tenants !== undefined && tenants.length !== 0) {
      lscache.set(
        'tenantAdminName',
        getTenantsBySSOTokenResponse.data.getTenantsBySSOToken.tenantAdminName,
      );

      return tenants;
    } else {
      authStateStore.setState({
        ssoContext: {
          signInStatus: true,
          ssoError: {
            code: SSOResponseCode.NO_VALID_TENANTS,
            message: parsedToken.email,
          },
        },
      });
      return undefined;
    }
  }
  return undefined;
};

export const authenticateBySSOToken = async (
  tenantId: string,
): Promise<string | undefined> => {
  const ssoToken = await getSSOToken();

  if (ssoToken.token === undefined) {
    return undefined;
  }

  const internalClient = new ApolloClient({
    cache: new InMemoryCache(),
    link: httpLink,
  });

  let resp;
  try {
    resp = await internalClient.mutate({
      mutation: gql`
        mutation AuthenticateTenantAdminWithSSOToken(
          $input: AuthenticateWithSSOTokenInput
        ) {
          authenticateWithSSOToken(input: $input) {
            tenantName
            tenantAdminName
            tenantAdminEmail
            accessToken
            tokenType
            expiresInSeconds
            passwordChanged
          }
        }
      `,
      variables: {
        input: {
          tenantId: tenantId,
          ssoToken: ssoToken.token,
        },
      },
    });

    const authResponse = resp.data.authenticateWithSSOToken;
    const validity = authResponse?.expiresInSeconds as number;

    lscache.set('currentTenantName', authResponse?.tenantName);
    lscache.set('tenantAdminName', authResponse?.tenantAdminName);
    lscache.set('tenantAdminEmail', authResponse?.tenantAdminEmail);
    lscache.set('accessToken', authResponse?.accessToken, validity);
    if (authResponse.accessToken !== undefined) {
      lscache.set('currentTenantId', tenantId);
      lscache.set('currentTenantId', tenantId);
    } else {
      lscache.remove('currentTenantId');
    }
  } catch (_error) {
    lscache.remove('accessToken');
    lscache.remove('currentTenantId');
    lscache.set('switchedFromTenantId', 'not_available');

    return undefined;
  }

  return resp?.data.authenticateWithSSOToken.accessToken;
};
